import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../../app/slices/EnvironmentSlice";
import { selectTrainingLocalizationState } from "../../app/slices/TrainingLocalizationSlice";
import { selectIsLocalizationInitialized } from "../../app/slices/TrainingLocalizationSlice";
import {
  addInteractionWarning,
  InitializeErrors,
  InitializeWarnings,
  updateInteractionError,
  ValidationError,
} from "../../app/slices/ValidationSlice";
import {
  selectActors,
  selectAllPropPresets,
  selectSelectedElement,
  selectSelectedElementIsInteraction,
  selectSelectedElementType,
  selectSelectedTake,
  selectSelectedTake3D,
  selectTakes,
  selectTrainingType,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import { getInteractionFromFactory } from "../interaction_factory/InteractionFactory";
import Interaction from "../../models/interactions/Interaction";
import { ISequence } from "../../models/interactions/Sequence";
import { useTrainingLocalization } from "../trainingLocalization/TrainingLocalizationHooks";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import Take from "../../models/Take";
import Take3D, {
  getTake3DSequence,
  isTake3DTransitionEnabled,
} from "../../models/Take3D";
import type { RootState } from "../../app/Store";
import { getClipboardReferenceValidationErrors } from "../training_timeline/clipboardDependencies";

export default function useValidationRoot() {
  const dispatch = useDispatch();
  const locState = useSelector(selectTrainingLocalizationState);
  const initialized = useSelector(selectIsLocalizationInitialized);
  const takes = useSelector(selectTakes);

  const { getValue } = useTrainingLocalization();
  const currentTake360 = useSelector(selectSelectedTake);
  const currentTake3D = useSelector(selectSelectedTake3D);
  const trainingType = useSelector(selectTrainingType);
  const actors = useSelector(selectActors);

  const selectedInteraction = useSelector(selectSelectedElement);
  const isInteraction = useSelector(selectSelectedElementIsInteraction);
  const selectedElementType = useSelector(selectSelectedElementType);

  const selectedTake3D = useSelector(selectSelectedTake3D);
  const selectedTake360 = useSelector(selectSelectedTake);

  const allPropPresets = useSelector(selectAllPropPresets);
  const rootState = useSelector((state: RootState) => state);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const { getSituationMetadata } = useSituationHelper();
  const previousTakeLocationRef = useRef<{
    takeId: string;
    locationId?: string;
  } | null>(null);
  // this validates everything at the start of a training
  useEffect(() => {
    if (!initialized) return;
    const validationResults = validateArrayOfTakes();
    dispatch(InitializeWarnings(validationResults.warnings));
    dispatch(InitializeErrors(validationResults.errors));
  }, [initialized]);

  // this revalidates the whole take when its location (interactionPointId) changes
  useEffect(() => {
    if (trainingType !== TrainingType.Training3D || !selectedTake3D) {
      previousTakeLocationRef.current = null;
      return;
    }

    const previous = previousTakeLocationRef.current;
    previousTakeLocationRef.current = {
      takeId: selectedTake3D.id,
      locationId: selectedTake3D.interactionPointId,
    };

    if (
      previous &&
      previous.takeId === selectedTake3D.id &&
      previous.locationId !== selectedTake3D.interactionPointId
    ) {
      revalidateTake(selectedTake3D);
    }
  }, [selectedTake3D, trainingType]);

  // Create a stable comparison key for the selected interaction
  const interactionKey = selectedInteraction
    ? `${(selectedInteraction as Interaction).id}-${JSON.stringify(
        selectedInteraction,
      )}`
    : "";

  // this validates whenever a selected interaction changes (data or selection), or loc state
  useEffect(() => {
    updateWarningsForSelectedInteraction();
    updateErrorsForSelectedInteraction();
  }, [
    interactionKey,
    isInteraction,
    locState.localizedEntries,
    selectedElementType,
  ]);

  const updateWarningsForSelectedInteraction = () => {
    if (!isInteraction && selectedElementType !== "takeSequence") return;
    if (selectedInteraction === undefined) return;
    const interaction = selectedInteraction as Interaction;
    const currentTake =
      trainingType === TrainingType.Training3D
        ? selectedTake3D
        : selectedTake360;
    if (!currentTake) return;
    if (
      selectedElementType === "transition" &&
      !isTake3DTransitionEnabled(currentTake as Take3D)
    ) {
      dispatch(
        addInteractionWarning({ interactionId: interaction.id, warnings: [] }),
      );
      return;
    }
    const warnings = getInteractionWarnings(
      selectedInteraction as Interaction,
      currentTake,
    );
    dispatch(
      addInteractionWarning({ interactionId: interaction.id, warnings }),
    );
  };

  const updateErrorsForSelectedInteraction = () => {
    if (!isInteraction && selectedElementType !== "takeSequence") return;
    if (!selectedInteraction) return;
    const casted = selectedInteraction as Interaction;
    const entity = getInteractionFromFactory(casted.type);
    const take =
      trainingType === TrainingType.Training3D
        ? selectedTake3D
        : selectedTake360;
    if (!entity || !take) return;
    if (
      selectedElementType === "transition" &&
      !isTake3DTransitionEnabled(take as Take3D)
    ) {
      dispatch(
        updateInteractionError({ interactionId: casted.id, errors: [] }),
      );
      return;
    }
    const errors = getInteractionErrors(casted, take, take);
    dispatch(updateInteractionError({ interactionId: casted.id, errors }));
  };

  const validateArrayOfTakes = () => {
    const validatedInteractions: Record<string, string[]> = {};
    const validatedErrorInteractions: Record<string, ValidationError[]> = {};
    takes.forEach((take) => {
      const interactionsToValidate = [
        ...getTakeOwnedInteractions(take),
        ...take.interactions,
      ];

      interactionsToValidate.forEach((interaction) => {
        validatedInteractions[interaction.id] = getInteractionWarnings(
          interaction,
          take,
        );
        validatedErrorInteractions[interaction.id] = getInteractionErrors(
          interaction,
          take,
          take,
        );
      });
    });

    return {
      warnings: validatedInteractions,
      errors: validatedErrorInteractions,
    };
  };

  // validates everything owned by a single take and merges the results into
  // the validation state, leaving other takes' entries untouched
  const revalidateTake = (take: Take) => {
    const interactionsToValidate = [
      ...getTakeOwnedInteractions(take),
      ...take.interactions,
    ];

    interactionsToValidate.forEach((interaction) => {
      dispatch(
        addInteractionWarning({
          interactionId: interaction.id,
          warnings: getInteractionWarnings(interaction, take),
        })
      );
      dispatch(
        updateInteractionError({
          interactionId: interaction.id,
          errors: getInteractionErrors(interaction, take, take),
        })
      );
    });

    const mainCamera = (take as Partial<Take3D>).mainCamera;
    if (mainCamera) {
      dispatch(
        updateInteractionError({
          interactionId: mainCamera.id,
          errors: getMainCameraLocationErrors(take as Take3D),
        })
      );
    }
  };

  // the main camera must point at a camera position of the take's location
  // (or of the situation assigned to that location)
  const getMainCameraLocationErrors = (take: Take3D): ValidationError[] => {
    const cameraId = take.mainCamera?.cameraId;
    if (!cameraId || !take.interactionPointId) return [];

    const location = takePositions.find(
      (point) => point.id === take.interactionPointId
    );
    if (!location) return [];

    const situationId = take.situations?.[location.id];
    const situationCameraIds = situationId
      ? (getSituationMetadata(situationId)?.camera_positions ?? []).map(
          (camera) => `${situationId}/${camera.id}`
        )
      : [];
    const validCameraIds = [
      ...location.camera_positions.map((camera) => camera.id),
      ...situationCameraIds,
    ];

    return validCameraIds.includes(cameraId)
      ? []
      : [{ message: "Main camera is not available at the take's location" }];
  };

  const getInteractionWarnings = (
    interaction: Interaction,
    currentTake: Take,
  ): string[] => {
    const factoryEntity = getInteractionFromFactory(interaction.type);
    if (!factoryEntity) return [];

    return [
      ...new Set(
        factoryEntity.Validate?.(
          interaction,
          getValue,
          currentTake,
          actors,
          allPropPresets,
        ),
      ),
    ];
  };

  const getInteractionErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
  ): ValidationError[] => {
    const entity = getInteractionFromFactory(interaction.type);
    if (!entity) return [];
    const factoryErrors =
      entity.ValidateErrors?.(
        interaction,
        parentTake,
        currentTake,
        actors,
        allPropPresets,
        locState.localizedEntries,
      ) ?? [];
    const referenceErrors = getClipboardReferenceValidationErrors(
      interaction,
      rootState,
      currentTake,
    );
    const errors = [...factoryErrors, ...referenceErrors];

    // Convert string errors to ValidationError format
    return errors.map((error) =>
      typeof error === "string" ? { message: error } : error,
    );
  };

  return {};
}

const getTakeOwnedSequences = (take: Take): ISequence[] => {
  const take3D = take as Partial<Take3D>;
  const sequence = getTake3DSequence(take3D as Pick<Take3D, "sequence">);
  return sequence ? [sequence] : [];
};

const getTakeOwnedInteractions = (take: Take): Interaction[] => {
  const take3D = take as Partial<Take3D>;
  const interactions: Interaction[] = [];

  if (
    take3D.transition &&
    isTake3DTransitionEnabled(take3D as Pick<Take3D, "properties">)
  ) {
    interactions.push(take3D.transition);
  }

  return [...interactions, ...getTakeOwnedSequences(take)];
};
